import { Injectable } from '@angular/core'
import { ProgressiveProfileService } from './progressive-profile.service'
import { UserInputService } from './user-input.service'

export interface StepValidationResult {
  step: number
  valid: boolean
  invalidFields: string[]
}

/**
 * Centralized service that determines which registration step has missing
 * mandatory data by checking UserInputService stored values.
 *
 * Validation is fully dynamic — driven entirely by the PP questions API `mandatory` field.
 * Fields are categorized into steps based on their mappingField.
 * Conditional logic handles admission test and english test dependent fields.
 */
@Injectable({
  providedIn: 'root'
})
export class StepValidationService {
  // Step categorization — maps each mappingField to its step number
  private readonly STEP_MAP: { [key: string]: number } = {
    // Step 1: Personal Details
    first_name: 1,
    last_name: 1,
    primary_mobile_number: 1,
    title: 1,
    date_of_birth: 1,
    gender: 1,
    maritalStatus: 1,
    nationalityCode: 1,
    preferredCountryCode: 1,
    interestedIDPSerivce: 1,
    preferredStudyLevel: 1,
    studyPlanTimeline: 1,
    passport: 1,
    dialCode: 1,
    primary_email: 1,
    modeOfCounselling: 1,
    primaryFinancialSource: 1,
    howDidYouHear: 1,
    // Step 2: Academic Details
    studyCountryName: 2,
    qualificationType: 2,
    qualificationTitle: 2,
    institutionName: 2,
    majorSubjects: 2,
    examBoardName: 2,
    studyMode: 2,
    studyStatus: 2,
    studyStartDate: 2,
    studyEndDate: 2,
    studyDuration: 2,
    gradeType: 2,
    finalGrade: 2,
    studyLanguageName: 2,
    applicableGrade: 2,
    isHonoursDegree: 2,
    numberOfBacklogsOrRetakes: 2,
    numberOfBacklogsRetakesInLast2Years: 2,
    // Step 3: English Proficiency
    englishIsFirstLanguage: 3,
    // Step 4: Other Details — removed (fields moved to step 1)
  }

  // Fields handled before the form (not part of step validation)
  private readonly PRE_FORM_FIELDS = new Set([
    'modeOfCounselling', 'primary_email', 'title', 'dialCode'
  ])

  // Step 2 fields that are conditionally visible (depend on qualification type / refdata)
  private readonly STEP2_CONDITIONAL = new Set([
    'qualificationType', 'qualificationTitle',
    'institutionName', 'majorSubjects', 'examBoardName',
    'gradeType', 'finalGrade', 'studyLanguageName', 'applicableGrade', 'isHonoursDegree',
    'studyEndDate', 'studyDuration',
    'numberOfBacklogsOrRetakes', 'numberOfBacklogsRetakesInLast2Years'
  ])

  constructor (
    private progressiveProfileService: ProgressiveProfileService,
    private userInputService: UserInputService
  ) {}

  /**
   * Finds the first step (1-4) that has missing mandatory data.
   * Returns 0 if all steps are valid.
   * Returns -1 if questions are not loaded (cannot validate).
   */
  getFirstInvalidStep (): number {
    const questions = this.getQuestions()
    if (!questions || questions.length === 0) {
      console.log('[StepValidation] Questions not loaded — cannot validate')
      return -1
    }
    if (this.isStep1Invalid(questions)) {
      console.log('[StepValidation] Step 1 invalid')
      return 1
    }
    if (this.isStep2Invalid(questions)) {
      console.log('[StepValidation] Step 2 invalid')
      return 2
    }
    if (this.isStep3Invalid(questions)) {
      console.log('[StepValidation] Step 3 invalid')
      return 3
    }
    if (this.isStep4Invalid(questions)) {
      console.log('[StepValidation] Step 4 invalid')
      return 4
    }
    console.log('[StepValidation] All steps valid')
    return 0
  }

  /**
   * Step 1: Personal Details
   * Validates all mandatory PP questions that belong to step 1.
   * Skips pre-form fields (email, dialCode, title, modeOfCounselling)
   * as they are handled before the registration form.
   */
  private isStep1Invalid (questions: any[]): boolean {
    for (const q of questions) {
      if (!q.mandatory) continue
      if (this.PRE_FORM_FIELDS.has(q.mappingField)) continue
      if (this.getStep(q) !== 1) continue
      if (this.isEmpty(q.mappingField)) return true
    }
    return false
  }

  /**
   * Step 2: Academic Details
   * Validates all mandatory PP questions that belong to step 2.
   * Conditional fields are validated based on their parent field visibility.
   */
  private isStep2Invalid (questions: any[]): boolean {
    const hasCountry = !this.isEmpty('studyCountryName')
    const hasQualification = !this.isEmpty('qualificationType')

    for (const q of questions) {
      if (!q.mandatory) continue
      if (this.getStep(q) !== 2) continue

      if (this.STEP2_CONDITIONAL.has(q.mappingField)) {
        // qualificationType is visible when country is selected
        if (q.mappingField === 'qualificationType') {
          if (hasCountry && this.isEmpty(q.mappingField)) return true
          continue
        }
        // qualificationTitle, institutionName, majorSubjects depend on qualification
        if (q.mappingField === 'qualificationTitle' || q.mappingField === 'institutionName' || q.mappingField === 'majorSubjects') {
          if (hasQualification && this.isEmpty(q.mappingField)) return true
          continue
        }
        // Other conditional fields — only validate if user has interacted
        const value = this.userInputService.getValue(q.mappingField)
        if (value === undefined) continue
        if (this.isEmpty(q.mappingField)) return true
      } else {
        // Always-visible fields (studyCountryName, studyStartDate, studyMode, studyStatus)
        if (this.isEmpty(q.mappingField)) return true
      }
    }

    // Admission test conditional logic
    if (this.isAdmissionTestConditionallyInvalid()) return true

    return false
  }

  /**
   * Admission test: when user selects a status, dependent fields become required.
   */
  private isAdmissionTestConditionallyInvalid (): boolean {
    const academicTestType = this.userInputService.getValue('academic_testType')
    const testTypeVal = this.extractLabel(academicTestType)
    const isNotTaken = academicTestType?.option_value === 'tt_17' || (academicTestType?.option_desc || testTypeVal)?.toLowerCase() === 'not taken'
    if (!testTypeVal || isNotTaken) return false
    if (this.isEmpty('academic_testTakenDate')) return true
    if (this.isEmpty('academic_overallScore')) return true
    return false
  }

  /**
   * Step 3: English Proficiency
   * Validates mandatory fields and conditional test fields.
   */
  private isStep3Invalid (questions: any[]): boolean {
    // Check englishIsFirstLanguage — only required if PP says mandatory
    const englishQ = questions.find((q: any) => q.mappingField === 'englishIsFirstLanguage')
    if (englishQ?.mandatory && this.isEmpty('englishIsFirstLanguage')) return true

    const englishVal = this.extractValue(this.userInputService.getValue('englishIsFirstLanguage'))
    if (englishVal?.toLowerCase() !== 'no') return false

    // Conditional logic for english test fields
    if (this.isEnglishTestConditionallyInvalid()) return true

    return false
  }

  /**
   * English test: when testType is selected and not 'not taken', dependent fields become required.
   */
  private isEnglishTestConditionallyInvalid (): boolean {
    const englishTestType = this.userInputService.getValue('english_testType')
    const testTypeVal = this.extractLabel(englishTestType)
    const isNotTaken = englishTestType?.option_value === 'tt_16' || (englishTestType?.option_desc || testTypeVal)?.toLowerCase() === 'not taken'
    if (!testTypeVal || isNotTaken) return false

    // If testType is "Others" — otherTestName is required
    if (testTypeVal.toLowerCase() === 'others') {
      if (this.isEmpty('other_english_testType')) return true
    }

    // testTakenDate and overallScore required when testType is set and not 'not taken'
    if (this.isEmpty('english_testTakenDate')) return true
    if (this.isEmpty('english_overallScore')) return true

    return false
  }

  /**
   * Step 4: Other Details — removed, fields moved to Personal Details (step 1)
   */
  private isStep4Invalid (_questions: any[]): boolean {
    return false
  }

  // --- Utility methods ---

  /**
   * Determines which step a question belongs to based on its mappingField.
   * Returns 0 if the field doesn't belong to any step (e.g., checkboxes, test fields).
   */
  private getStep (question: any): number {
    return this.STEP_MAP[question.mappingField] || 0
  }

  private getQuestions (): any[] | undefined {
    return this.progressiveProfileService.serviceQuestions?.items?.[0]?.fields?.questions
  }

  private isEmpty (key: string): boolean {
    const value = this.userInputService.getValue(key)
    if (value === null || value === undefined) return true
    if (typeof value === 'string') return value.trim() === ''
    if (typeof value === 'boolean') return false
    if (typeof value === 'number') return false
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') {
      return !value.option_value && !value.option_label && !value.option_desc
    }
    return true
  }

  private extractValue (value: any): string | null {
    if (!value) return null
    if (typeof value === 'string') return value
    return value.option_value || value.option_label || null
  }

  private extractLabel (value: any): string | null {
    if (!value) return null
    if (typeof value === 'string') return value
    return value.option_label || value.option_value || null
  }
}
